// =============================================================================
// TeamColorSvgHero — fallback hero/image when no real DAM image exists.
// =============================================================================
// Universal default visual for any article surface (lobby card, hero card,
// article page, video card poster) when `heroImage.url` is missing OR fails
// to load. Composes two smooth gradients — one per team — into a 2:1 SVG.
//
// COLOR MODEL — 3 colors per team
// -------------------------------
// Each team has three color slots: `[color1, color2, color3]`.
//   - Country (flag): three distinct flag colors (USA → red, white, blue).
//   - Club (kit): primary kit color in slots 1+3, secondary in slot 2
//     (Manchester City → lightblue, white, lightblue).
//
// Resolution order:
//   1. Explicit `homeColors` / `awayColors` props (engine-emitted when
//      the schema extension ships).
//   2. Static lookup in `team-colors-data.ts` by team URN or name (the
//      current source of truth — 48 WC nations + selected clubs).
//   3. Hash-derived fallback for unknown teams. Slot 2 stays near-white
//      so the visual still reads like a flag/kit, not random tie-dye.
//
// PATTERNS — smooth gradients only
// --------------------------------
// 10 deterministic compositions of two 3-stop gradients (home + away).
// No hard edges, no stripes, no dot grids. The composition varies
// (linear-diagonal, radial, dual-corner radial, vertical, conic-like
// fan, etc.) so the lobby has visual variety without ever showing
// blocky / patterned imagery.
// =============================================================================

import { type ReactElement } from 'react';
import { lookupTeamColors, type TeamColorTriple } from './team-colors-data.js';

// ----------------------------------------------------------------------------
// Hashing primitives (used only when neither explicit nor static colors hit)
// ----------------------------------------------------------------------------

function hash32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}

function safeHue(hue: number): number {
  const h = ((hue % 360) + 360) % 360;
  if (h >= 50 && h <= 70) return h + 30;
  if (h >= 170 && h <= 200) return h + 30;
  return h;
}

function hashedColors(key: string, isAway: boolean): TeamColorTriple {
  const h = hash32(key);
  const hue1 = (h + (isAway ? 137 : 0)) % 360;
  const hue3 = (hue1 + 173) % 360;
  const sat = 58 + (h % 14);
  const light = 38 + ((h >>> 8) % 12);
  return [
    `hsl(${safeHue(hue1)}deg ${sat}% ${light}%)`,
    `hsl(215deg 18% 96%)`,
    `hsl(${safeHue(hue3)}deg ${sat}% ${light - 4}%)`,
  ];
}

function resolveColors(
  explicit: TeamColorTriple | undefined,
  urn: string | undefined,
  name: string | undefined,
  isAway: boolean,
): TeamColorTriple {
  if (explicit) return explicit;
  const fromUrn = lookupTeamColors(urn);
  if (fromUrn) return fromUrn;
  const fromName = lookupTeamColors(name);
  if (fromName) return fromName;
  return hashedColors(name ?? urn ?? (isAway ? 'away' : 'home'), isAway);
}

// ----------------------------------------------------------------------------
// Pattern renderers — all gradient-only.
// ----------------------------------------------------------------------------
//
// Each renderer returns a <defs>-and-rects sub-tree, taking unique id
// prefixes so multiple SVGs on the same page don't collide on gradient
// element ids.

type Triple = readonly [string, string, string];
type PatternProps = { h: Triple; a: Triple; uid: string };
type PatternRenderer = (p: PatternProps) => ReactElement;

const PATTERNS: PatternRenderer[] = [
  // 0 — Horizontal blend: home gradient left → away gradient right,
  //     soft cross-fade through the middle band.
  ({ h, a, uid }) => (
    <g>
      <defs>
        <linearGradient id={`${uid}-h`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={h[0]} />
          <stop offset="55%" stopColor={h[1]} />
          <stop offset="100%" stopColor={h[2]} />
        </linearGradient>
        <linearGradient id={`${uid}-a`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={a[0]} />
          <stop offset="55%" stopColor={a[1]} />
          <stop offset="100%" stopColor={a[2]} />
        </linearGradient>
        <linearGradient id={`${uid}-fade`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="white" stopOpacity="0" />
          <stop offset="50%" stopColor="white" stopOpacity="0" />
          <stop offset="100%" stopColor="white" stopOpacity="1" />
        </linearGradient>
        <mask id={`${uid}-mask`}>
          <rect width="400" height="200" fill={`url(#${uid}-fade)`} />
        </mask>
      </defs>
      <rect width="400" height="200" fill={`url(#${uid}-h)`} />
      <rect width="400" height="200" fill={`url(#${uid}-a)`} mask={`url(#${uid}-mask)`} />
    </g>
  ),

  // 1 — Diagonal blend top-left → bottom-right. Each team's gradient
  //     uses the diagonal axis so the colors meet along the seam.
  ({ h, a, uid }) => (
    <g>
      <defs>
        <linearGradient id={`${uid}-h`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={h[0]} />
          <stop offset="40%" stopColor={h[1]} />
          <stop offset="100%" stopColor={h[2]} />
        </linearGradient>
        <linearGradient id={`${uid}-a`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={a[2]} stopOpacity="0" />
          <stop offset="55%" stopColor={a[1]} stopOpacity="0.85" />
          <stop offset="100%" stopColor={a[0]} stopOpacity="1" />
        </linearGradient>
      </defs>
      <rect width="400" height="200" fill={`url(#${uid}-h)`} />
      <rect width="400" height="200" fill={`url(#${uid}-a)`} />
    </g>
  ),

  // 2 — Two radials from opposite corners. Home from top-left,
  //     away from bottom-right. Each fades out toward the centre.
  ({ h, a, uid }) => (
    <g>
      <defs>
        <radialGradient id={`${uid}-h`} cx="0" cy="0" r="1.1">
          <stop offset="0%" stopColor={h[0]} stopOpacity="1" />
          <stop offset="55%" stopColor={h[2]} stopOpacity="0.85" />
          <stop offset="100%" stopColor={h[1]} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${uid}-a`} cx="1" cy="1" r="1.1">
          <stop offset="0%" stopColor={a[0]} stopOpacity="1" />
          <stop offset="55%" stopColor={a[2]} stopOpacity="0.85" />
          <stop offset="100%" stopColor={a[1]} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="400" height="200" fill={h[1]} />
      <rect width="400" height="200" fill={`url(#${uid}-h)`} />
      <rect width="400" height="200" fill={`url(#${uid}-a)`} />
    </g>
  ),

  // 3 — Vertical blend, home top → away bottom. Each side blends
  //     through its own 3 stops.
  ({ h, a, uid }) => (
    <g>
      <defs>
        <linearGradient id={`${uid}-g`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={h[0]} />
          <stop offset="22%" stopColor={h[1]} />
          <stop offset="42%" stopColor={h[2]} />
          <stop offset="58%" stopColor={a[2]} />
          <stop offset="78%" stopColor={a[1]} />
          <stop offset="100%" stopColor={a[0]} />
        </linearGradient>
      </defs>
      <rect width="400" height="200" fill={`url(#${uid}-g)`} />
    </g>
  ),

  // 4 — Centered radial spotlight: home radial at centre-left,
  //     away radial at centre-right.
  ({ h, a, uid }) => (
    <g>
      <defs>
        <radialGradient id={`${uid}-h`} cx="0.25" cy="0.5" r="0.7">
          <stop offset="0%" stopColor={h[0]} stopOpacity="1" />
          <stop offset="60%" stopColor={h[2]} stopOpacity="0.85" />
          <stop offset="100%" stopColor={h[1]} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${uid}-a`} cx="0.75" cy="0.5" r="0.7">
          <stop offset="0%" stopColor={a[0]} stopOpacity="1" />
          <stop offset="60%" stopColor={a[2]} stopOpacity="0.85" />
          <stop offset="100%" stopColor={a[1]} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="400" height="200" fill={h[1]} />
      <rect width="400" height="200" fill={`url(#${uid}-h)`} />
      <rect width="400" height="200" fill={`url(#${uid}-a)`} />
    </g>
  ),

  // 5 — Diagonal counter-blend: home diagonal one way, away the
  //     opposite way. Soft cross-fade.
  ({ h, a, uid }) => (
    <g>
      <defs>
        <linearGradient id={`${uid}-h`} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor={h[0]} />
          <stop offset="50%" stopColor={h[1]} />
          <stop offset="100%" stopColor={h[2]} />
        </linearGradient>
        <linearGradient id={`${uid}-a`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={a[2]} stopOpacity="0" />
          <stop offset="100%" stopColor={a[0]} stopOpacity="0.92" />
        </linearGradient>
      </defs>
      <rect width="400" height="200" fill={`url(#${uid}-h)`} />
      <rect width="400" height="200" fill={`url(#${uid}-a)`} />
    </g>
  ),

  // 6 — Radial centre spot: home in the centre, away around the edges.
  ({ h, a, uid }) => (
    <g>
      <defs>
        <radialGradient id={`${uid}-h`} cx="0.5" cy="0.5" r="0.65">
          <stop offset="0%" stopColor={h[0]} />
          <stop offset="55%" stopColor={h[2]} />
          <stop offset="100%" stopColor={h[1]} stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${uid}-a`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={a[0]} />
          <stop offset="50%" stopColor={a[1]} />
          <stop offset="100%" stopColor={a[2]} />
        </linearGradient>
      </defs>
      <rect width="400" height="200" fill={`url(#${uid}-a)`} />
      <rect width="400" height="200" fill={`url(#${uid}-h)`} />
    </g>
  ),

  // 7 — Smooth wave-shape mask between two linear gradients.
  //     Home gradient covers the left ~60% along a sine-curve seam,
  //     away gradient on the right.
  ({ h, a, uid }) => (
    <g>
      <defs>
        <linearGradient id={`${uid}-h`} x1="0" y1="0" x2="1" y2="0.4">
          <stop offset="0%" stopColor={h[0]} />
          <stop offset="50%" stopColor={h[1]} />
          <stop offset="100%" stopColor={h[2]} />
        </linearGradient>
        <linearGradient id={`${uid}-a`} x1="0" y1="0.6" x2="1" y2="1">
          <stop offset="0%" stopColor={a[2]} />
          <stop offset="50%" stopColor={a[1]} />
          <stop offset="100%" stopColor={a[0]} />
        </linearGradient>
        <clipPath id={`${uid}-clip`}>
          <path d="M0,0 L240,0 C 230,60 270,140 240,200 L0,200 Z" />
        </clipPath>
      </defs>
      <rect width="400" height="200" fill={`url(#${uid}-a)`} />
      <g clipPath={`url(#${uid}-clip)`}>
        <rect width="400" height="200" fill={`url(#${uid}-h)`} />
      </g>
      {/* Soft feather along the wave seam */}
      <defs>
        <linearGradient id={`${uid}-feather`} x1="0.45" y1="0" x2="0.7" y2="0">
          <stop offset="0%" stopColor={h[1]} stopOpacity="0.45" />
          <stop offset="100%" stopColor={a[1]} stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect width="400" height="200" fill={`url(#${uid}-feather)`} />
    </g>
  ),

  // 8 — Concentric soft spots: home glow upper-left, away glow lower-right,
  //     centre tone is the merge.
  ({ h, a, uid }) => (
    <g>
      <defs>
        <radialGradient id={`${uid}-h`} cx="0.2" cy="0.3" r="0.85">
          <stop offset="0%" stopColor={h[0]} stopOpacity="1" />
          <stop offset="50%" stopColor={h[2]} stopOpacity="0.6" />
          <stop offset="100%" stopColor={h[1]} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${uid}-a`} cx="0.8" cy="0.7" r="0.85">
          <stop offset="0%" stopColor={a[0]} stopOpacity="1" />
          <stop offset="50%" stopColor={a[2]} stopOpacity="0.6" />
          <stop offset="100%" stopColor={a[1]} stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${uid}-base`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={h[1]} />
          <stop offset="100%" stopColor={a[1]} />
        </linearGradient>
      </defs>
      <rect width="400" height="200" fill={`url(#${uid}-base)`} />
      <rect width="400" height="200" fill={`url(#${uid}-h)`} />
      <rect width="400" height="200" fill={`url(#${uid}-a)`} />
    </g>
  ),

  // 9 — Sunset / sunrise blend: home gradient top with depth, away
  //     gradient bottom, joined by a soft horizontal seam.
  ({ h, a, uid }) => (
    <g>
      <defs>
        <linearGradient id={`${uid}-top`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={h[0]} />
          <stop offset="60%" stopColor={h[2]} />
          <stop offset="100%" stopColor={h[1]} stopOpacity="0.85" />
        </linearGradient>
        <linearGradient id={`${uid}-bot`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={a[1]} stopOpacity="0.85" />
          <stop offset="40%" stopColor={a[2]} />
          <stop offset="100%" stopColor={a[0]} />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="400" height="120" fill={`url(#${uid}-top)`} />
      <rect x="0" y="80" width="400" height="120" fill={`url(#${uid}-bot)`} />
    </g>
  ),
];

// ----------------------------------------------------------------------------
// Public component
// ----------------------------------------------------------------------------

export interface TeamColorSvgHeroProps {
  /** Stable identifier for the home side. Used for color lookup +
   *  hash fallback. Can be team URN or team name. */
  homeKey: string;
  /** Stable identifier for the away side. */
  awayKey: string;
  /** Stable identifier for this fixture/article — drives pattern selection. */
  eventId: string;

  /** Optional Sportradar URN for the home team (preferred lookup key
   *  for static color data). When omitted, `homeKey` is tried by name. */
  homeUrn?: string | undefined;
  awayUrn?: string | undefined;

  /** Optional explicit home team colors [c1, c2, c3]. Highest priority
   *  — overrides both static lookup and hash fallback. Engine schema
   *  extension will emit these on team objects. */
  homeColors?: TeamColorTriple | undefined;
  awayColors?: TeamColorTriple | undefined;

  /** Optional aria-label override. Default: "{home} vs {away}". */
  ariaLabel?: string | undefined;
  /** Optional className for sizing — the SVG fills its parent by default. */
  className?: string | undefined;
}

export function TeamColorSvgHero({
  homeKey,
  awayKey,
  eventId,
  homeUrn,
  awayUrn,
  homeColors,
  awayColors,
  ariaLabel,
  className,
}: TeamColorSvgHeroProps): ReactElement {
  const home = resolveColors(homeColors, homeUrn, homeKey, false);
  const away = resolveColors(awayColors, awayUrn, awayKey, true);
  const patternSeed = hash32(eventId || `${homeKey}::${awayKey}`);
  const patternIndex = patternSeed % PATTERNS.length;
  const Pattern = PATTERNS[patternIndex]!;
  // Each SVG instance needs its own gradient id namespace; otherwise
  // multiple cards on the same page collide on `<defs id="...">`.
  const uid = `tch-${(patternSeed & 0xffffff).toString(36)}`;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 400 200"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-label={ariaLabel ?? `${homeKey} vs ${awayKey}`}
      className={className}
      style={{ display: 'block', width: '100%', height: '100%' }}
    >
      <Pattern h={home} a={away} uid={uid} />
    </svg>
  );
}
